import { useEffect, type ReactNode } from 'react';
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest, isEntraConfigured } from './msalConfig';
import { stashDeepLink, takeStashedDeepLink } from './deepLinkStash';

interface Props {
  children: ReactNode;
}

function stashDeepLinkAndLogin(instance: ReturnType<typeof useMsal>['instance']) {
  stashDeepLink();
  instance.loginRedirect(loginRequest);
}

/**
 * Wraps children so they only render after Entra ID authentication.
 * In dev (no Entra config), renders children immediately.
 */
export function AuthGuard({ children }: Props) {
  const { instance, inProgress } = useMsal();

  // Dev mode: no Entra configured, pass through
  if (!isEntraConfigured) {
    return <>{children}</>;
  }

  // Waiting for redirect / token acquisition
  if (inProgress !== InteractionStatus.None) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#999', fontFamily: "'DM Mono', monospace" }}>
        Signing in...
      </div>
    );
  }

  return (
    <>
      <AuthenticatedTemplate>
        <DeepLinkRestorer />
        {children}
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 16,
          fontFamily: "'DM Sans', sans-serif",
        }}>
          <img src="/ei-logo.svg" alt="Ember Infrastructure" style={{ width: 300 }} />
          <h1 style={{ fontSize: 22, fontWeight: 600, color: '#1A1A1A' }}>
            Ember Deal Pipeline
          </h1>
          <p style={{ color: '#666', fontSize: 14 }}>
            Sign in with your Ember Infrastructure account to continue.
          </p>
          <button
            onClick={() => stashDeepLinkAndLogin(instance)}
            style={{
              padding: '10px 24px',
              fontSize: 14,
              fontWeight: 600,
              borderRadius: 6,
              border: 'none',
              background: '#1A1A1A',
              color: '#fff',
              cursor: 'pointer',
            }}
          >
            Sign in with Microsoft
          </button>
        </div>
      </UnauthenticatedTemplate>
    </>
  );
}

/**
 * Restores the pre-login deep-link target (if any) on the first authenticated
 * render. Only runs when the user lands at the bare origin — if they already
 * navigated, we leave them where they are.
 */
function DeepLinkRestorer() {
  useEffect(() => {
    const stashed = takeStashedDeepLink();
    if (!stashed) return;
    const atBareOrigin =
      window.location.pathname === '/' &&
      !window.location.search &&
      !window.location.hash;
    if (!atBareOrigin) return;
    window.history.replaceState(null, '', stashed);
    // Notify useTabRouter — replaceState doesn't fire popstate natively,
    // but useTabRouter listens to it for URL changes.
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, []);
  return null;
}
